import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ImportResult, Category } from '../types';
import { apiService } from '../services/api';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { ScrollableSelect } from '../components/ScrollableSelect';

const BREADCRUMBS = [
  { label: 'Home', href: '/' },
  { label: 'Importar Contatos' },
];

export function ContactsImportPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [duplicateAction, setDuplicateAction] = useState<'skip' | 'update_category'>('skip');
  const [duplicateCategoriaId, setDuplicateCategoriaId] = useState<string>('');

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Sem categoria' },
      ...categories
        .filter((c) => c.nome.toLowerCase() !== 'sem categoria')
        .map((c) => ({ value: c.id, label: c.nome })),
    ],
    [categories]
  );

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const allCategories = await apiService.getAllCategories();
      setCategories(allCategories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const isCSV = selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv');
      const isXLSX = selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || selectedFile.name.endsWith('.xlsx');

      if (!isCSV && !isXLSX) {
        toast.error('Por favor, selecione um arquivo CSV ou XLSX válido');
        return;
      }
      setFile(selectedFile);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Selecione um arquivo');
      return;
    }

    // Navegar para página de mapeamento
    navigate('/contatos/importar/mapeamento', {
      state: {
        file,
        categoryId: selectedCategoryId,
        duplicateAction,
        duplicateCategoriaId
      }
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCSVTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-contatos.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      toast.error('Erro ao baixar template CSV');
    }
  };

  const handleDownloadTemplateXLSX = async () => {
    try {
      const blob = await apiService.downloadXLSXTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'TemplateContatos.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template XLSX baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template XLSX:', error);
      toast.error('Erro ao baixar template XLSX');
    }
  };

  return (
    <>
      <Header
        title="Importar Contatos"
        subtitle="Envie um arquivo CSV para adicionar vários contatos de uma vez"
        compactTopbar
      />

      <PageLayout>
        <PageHeader title="Importar Contatos" breadcrumbs={BREADCRUMBS} />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            {/* Cabeçalho */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Importar Contatos</h2>
            </div>

            <div className="p-5 sm:p-6 space-y-6">
            {/* Instruções no topo do conteúdo - estilo success */}
            <div className="rounded-xl border p-4 border-emerald-500 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/15">
              <div className="flex items-start gap-3">
                <div className="-mt-0.5 text-emerald-500">
                  <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M3.70186 12.0001C3.70186 7.41711 7.41711 3.70186 12.0001 3.70186C16.5831 3.70186 20.2984 7.41711 20.2984 12.0001C20.2984 16.5831 16.5831 20.2984 12.0001 20.2984C7.41711 20.2984 3.70186 16.5831 3.70186 12.0001ZM12.0001 1.90186C6.423 1.90186 1.90186 6.423 1.90186 12.0001C1.90186 17.5772 6.423 22.0984 12.0001 22.0984C17.5772 22.0984 22.0984 17.5772 22.0984 12.0001C22.0984 6.423 17.5772 1.90186 12.0001 1.90186ZM15.6197 10.7395C15.9712 10.388 15.9712 9.81819 15.6197 9.46672C15.2683 9.11525 14.6984 9.11525 14.347 9.46672L11.1894 12.6243L9.6533 11.0883C9.30183 10.7368 8.73198 10.7368 8.38051 11.0883C8.02904 11.4397 8.02904 12.0096 8.38051 12.3611L10.553 14.5335C10.7217 14.7023 10.9507 14.7971 11.1894 14.7971C11.428 14.7971 11.657 14.7023 11.8257 14.5335L15.6197 10.7395Z" />
                  </svg>
                </div>
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">Instruções</h4>
                  <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                    <li>• Colunas obrigatórias: <strong>nome</strong> e <strong>telefone</strong></li>
                    <li>• Opcionais: email, observacoes, categoriaId ou campos personalizados</li>
                    <li>• Telefone: código do país + DDD + número (ex: 5511999999999)</li>
                    <li>• Baixe um modelo abaixo, preencha e envie o arquivo .csv ou .xlsx</li>
                    <li>• Máximo 1.000 contatos por arquivo (até 1.005 linhas, incluindo cabeçalho)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Grid 2 colunas: Esquerda (Categoria + Duplicados) | Direita (Modelo + Upload) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Coluna esquerda: Categoria e Duplicados */}
              <div className="space-y-6">
                {/* Seletor de categoria */}
                <div>
                  <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Selecione a categoria
                  </label>
                  <ScrollableSelect
                    id="category-select"
                    value={selectedCategoryId}
                    onChange={setSelectedCategoryId}
                    placeholder="Sem categoria"
                    disabled={loadingCategories}
                    loading={loadingCategories}
                    options={categoryOptions}
                  />
                  {categories.length === 0 && !loadingCategories && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Nenhuma categoria cadastrada. Os contatos serão importados sem categoria.
                    </p>
                  )}
                </div>

                {/* Duplicados */}
                <div>
                  <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contatos duplicados</p>
                  <div className="rounded-xl border p-4 border-amber-500 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/15">
                    <div className="flex items-start gap-3">
                      <div className="-mt-0.5 text-amber-500">
                        <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M3.6501 12.0001C3.6501 7.38852 7.38852 3.6501 12.0001 3.6501C16.6117 3.6501 20.3501 7.38852 20.3501 12.0001C20.3501 16.6117 16.6117 20.3501 12.0001 20.3501C7.38852 20.3501 3.6501 16.6117 3.6501 12.0001ZM12.0001 1.8501C6.39441 1.8501 1.8501 6.39441 1.8501 12.0001C1.8501 17.6058 6.39441 22.1501 12.0001 22.1501C17.6058 22.1501 22.1501 17.6058 22.1501 12.0001C22.1501 6.39441 17.6058 1.8501 12.0001 1.8501ZM10.9992 7.52517C10.9992 8.07746 11.4469 8.52517 11.9992 8.52517H12.0002C12.5525 8.52517 13.0002 8.07746 13.0002 7.52517C13.0002 6.97289 12.5525 6.52517 12.0002 6.52517H11.9992C11.4469 6.52517 10.9992 6.97289 10.9992 7.52517ZM12.0002 17.3715C11.586 17.3715 11.2502 17.0357 11.2502 16.6215V10.945C11.2502 10.5308 11.586 10.195 12.0002 10.195C12.4144 10.195 12.7502 10.5308 12.7502 10.945V16.6215C12.7502 17.0357 12.4144 17.3715 12.0002 17.3715Z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Se o telefone já existir na base, o que fazer?</p>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="duplicateAction"
                              checked={duplicateAction === 'skip'}
                              onChange={() => setDuplicateAction('skip')}
                              className="text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Pular (não importar o duplicado)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="duplicateAction"
                              checked={duplicateAction === 'update_category'}
                              onChange={() => setDuplicateAction('update_category')}
                              className="text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Atualizar categoria do contato para:</span>
                          </label>
                          {duplicateAction === 'update_category' && (
                            <div className="ml-6 mt-1 max-w-xs">
                              <ScrollableSelect
                                value={duplicateCategoriaId}
                                onChange={setDuplicateCategoriaId}
                                placeholder="Selecione uma categoria"
                                options={categories.map((cat) => ({ value: cat.id, label: cat.nome }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coluna direita: Modelo e Upload */}
              <div className="space-y-6">
                {/* Modelo (template) */}
                <div className="h-[78px]">
                  <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Baixar modelo</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadTemplateXLSX}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:border-transparent transition-colors"
                    >
                      <i className="fas fa-file-excel text-base shrink-0" aria-hidden />
                      Baixar XLSX
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:border-transparent transition-colors"
                    >
                      <i className="fas fa-file-csv text-base shrink-0" aria-hidden />
                      Baixar CSV
                    </button>
                  </div>
                </div>

                {/* Upload de arquivo */}
                <div>
                  <label htmlFor="csv-file" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Carregar arquivo</label>
                  <div
                    className="border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl p-6 text-center hover:border-gray-300 dark:hover:border-gray-500 transition-colors bg-gray-50 dark:bg-gray-800/50 cursor-pointer h-full min-h-[200px] flex items-center justify-center"
                    onClick={() => document.getElementById('csv-file')?.click()}
                  >
                    <input
                      id="csv-file"
                      type="file"
                      accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <span className="block">
                      <i className="fas fa-cloud-arrow-up text-2xl text-gray-400 dark:text-gray-500 mb-2 block" aria-hidden />
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-medium block">Clique ou arraste o arquivo</span>
                      <span className="text-xs text-gray-500 dark:text-gray-500 mt-1 block">.csv ou .xlsx</span>
                    </span>
                  </div>
                  {file && (
                    <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center gap-2">
                      <i className="fas fa-check text-emerald-600 shrink-0" aria-hidden />
                      <span className="text-sm text-gray-700 dark:text-gray-200 font-medium truncate">{file.name}</span>
                    </div>
                  )}

                  {/* Botão Importar logo abaixo do arquivo */}
                  {file && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={handleImport}
                        disabled={isUploading}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-file-import text-base shrink-0" aria-hidden />
                            Importar Contatos
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Resultado */}
            {importResult && (
              <div className={`border rounded-xl p-4 ${importResult.success ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600'}`}>
                <h3 className="font-medium text-sm mb-3 text-gray-800 dark:text-white/90">
                  Resultado da importação
                </h3>
                <div className={`grid gap-2 mb-3 ${(importResult.skippedDuplicates ?? 0) + (importResult.updatedDuplicates ?? 0) > 0 ? 'grid-cols-5' : 'grid-cols-3'}`}>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center border border-gray-200 dark:border-gray-600">
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{importResult.totalRows}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center border border-gray-200 dark:border-gray-600">
                    <div className="text-lg font-bold text-emerald-600">{importResult.successfulImports}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Importados</div>
                  </div>
                  {(importResult.skippedDuplicates ?? 0) > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center border border-gray-200 dark:border-gray-600">
                      <div className="text-lg font-bold text-gray-700 dark:text-gray-300">{importResult.skippedDuplicates}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Pulados</div>
                    </div>
                  )}
                  {(importResult.updatedDuplicates ?? 0) > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center border border-gray-200 dark:border-gray-600">
                      <div className="text-lg font-bold text-gray-700 dark:text-gray-300">{importResult.updatedDuplicates}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Atualizados</div>
                    </div>
                  )}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-2 text-center border border-gray-200 dark:border-gray-600">
                    <div className="text-lg font-bold text-gray-700 dark:text-gray-300">{importResult.failedImports}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Erros</div>
                  </div>
                </div>
                {importResult.errors.length > 0 && (
                  <details className="cursor-pointer">
                    <summary className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Ver erros ({importResult.errors.length})</summary>
                    <div className="mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-2 max-h-40 overflow-y-auto">
                      {importResult.errors.map((error, index) => (
                        <div key={index} className="text-xs text-gray-600 dark:text-gray-400 py-1">{error}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </PageLayout>
    </>
  );
}
